import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { encryptId } from '@/lib/encryptId';
import { AlertTriangle, ArrowRight, BookOpen, ChevronLeft, ChevronRight, ExternalLink, FolderKanban, Info, Link as LinkIcon, Loader2, Plus, Search, TrendingUp, User, X } from 'lucide-react';

function Sk({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded bg-surface-muted ${className}`} />;
}
import {
  studies, studyLinks, type StudyRow, type StudyLinkCategory,
} from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  humanStatus, studyStatusBadgeClass, humanLinkCategory,
} from '@/lib/humanise';
import { CAN_CREATE_STUDY, CAN_MANAGE_STUDY_LINKS } from '@/lib/permissions';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import { PageHeader } from '@/components/layout/PageHeader';
import { toast } from 'sonner';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';
import { studyLabel } from '@/lib/studyLabel';

/**
 * DREAM-17 — Study Portfolio View.
 *
 * Per-site multi-study dashboard. Four summary tiles, report window
 * selector, aggregate enrollment funnel, lifecycle filter tabs over a
 * paginated + searchable study table, and a Study Links panel.
 *
 * Cross-cutting Sprint-1 rules applied:
 *  - All status / category / action strings go through humanise.ts.
 *  - Every mutation surfaces a sonner toast.
 *  - "+ New Study" gated behind <RoleGate>.
 */

type ReportWindowKey = 'this_week' | 'last_week' | 'weekly' | 'monthly' | 'custom';

const LIFECYCLE_OPTIONS: { key: string; label: string }[] = [
  { key: 'draft', label: 'Draft' },
  { key: 'initiation', label: 'Initiation' },
  { key: 'active', label: 'Active' },
  { key: 'closed_to_enrollment', label: 'Closed to Enrollment' },
  { key: 'follow_up', label: 'Follow-up' },
  { key: 'completed', label: 'Completed' },
  { key: 'archived', label: 'Archived' },
];

const WINDOW_OPTIONS: { key: ReportWindowKey; label: string }[] = [
  { key: 'this_week', label: 'This Week' },
  { key: 'last_week', label: 'Last Week' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'custom', label: 'Custom' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50];

export function StudyPortfolioPage() {
  const navigate = useNavigate();
  const perms = useMyPermissions();
  const { user } = useAuth();

  const [selectedLifecycles, setSelectedLifecycles] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [windowKey, setWindowKey] = useState<ReportWindowKey>('weekly');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  // Summary tiles + funnel (window-scoped). AC13: tile counts are computed
  // unfiltered server-side, so changing `lifecycle` does NOT requery this.
  const summary = useQuery({
    queryKey: ['portfolio-summary', windowKey, customFrom, customTo],
    queryFn: () => studies.portfolioSummary({
      window: windowKey,
      from: windowKey === 'custom' ? customFrom || undefined : undefined,
      to: windowKey === 'custom' ? customTo || undefined : undefined,
    }),
  });

  const lifecycleParam = selectedLifecycles.size === 0 ? 'all' : [...selectedLifecycles].join(',');

  // Paginated, filtered, searchable study table
  const list = useQuery({
    queryKey: ['portfolio', lifecycleParam, search, page, pageSize],
    queryFn: () => studies.portfolio({ lifecycle: lifecycleParam, search: search.trim() || undefined, page, pageSize }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Study portfolio"
        subtitle={`All studies at ${user?.siteNames?.[user.activeSiteId] ?? 'this site'}. Track progress, jump in, or onboard a new one.`}
        actions={
          // Checked on the EXACT operation, not the (studies, create) module
          // verb: that cell also holds ie_checklist.create and
          // schedule.visit.create, so a role with only those would see a button
          // whose submit the API refuses. CAN_CREATE_STUDY is the fallback for
          // an in-flight or failed /me/permissions.
          perms.holdsOperation('studies.create', CAN_CREATE_STUDY) ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate('/studies/new')}
            >
              <Plus className="h-4 w-4" /> New study
            </button>
          ) : null
        }
      />

      {/* Summary tiles — AC1, AC13 (independent of filter) */}
      <SummaryTiles
        loading={summary.isLoading}
        tiles={summary.data?.tiles}
      />

      {/* Report window selector — AC6 */}
      <ReportWindowSelector
        value={windowKey}
        onChange={setWindowKey}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
      />

      {/* Aggregate enrollment funnel — AC7 */}
      <EnrollmentFunnelWidget
        loading={summary.isLoading}
        funnel={summary.data?.funnel}
      />

      {/* Lifecycle multi-checkbox filter + search + table — AC8, AC9, AC10 */}
      <div className="card">
        <LifecycleCheckboxFilter
          selected={selectedLifecycles}
          onChange={(v) => { setSelectedLifecycles(v); setPage(1); }}
        />
        <div className="border-b border-surface-border bg-surface-muted/50 px-4 py-3">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
            <input
              type="text"
              className="input pl-9"
              placeholder="Search by protocol, sponsor, or title"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        </div>
        <StudyTable
          loading={list.isLoading}
          error={list.error}
          rows={list.data?.rows ?? []}
          total={list.data?.total ?? null}
          page={page}
          pageSize={pageSize}
          hasMore={list.data?.has_more ?? false}
          onPageChange={setPage}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        />
      </div>

      {/* Study Links — AC11, AC12 */}
      <StudyLinksPanel />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary Tiles (AC1, AC13)
// ---------------------------------------------------------------------------

function SummaryTiles({ loading, tiles }:
  { loading: boolean; tiles?: { total: number; active: number; onboarding: number; closeout: number; archived: number } }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Tile label="Total Studies" value={tiles?.total} loading={loading} icon={<FolderKanban className="h-4 w-4 text-ink-muted" />} />
      <Tile label="Active (Enrolling)" value={tiles?.active} loading={loading} accent="text-success-700" />
      <Tile label="Onboarding" value={tiles?.onboarding} loading={loading} accent="text-warning-700" />
      <Tile label="Closeout / Archival" value={(tiles?.closeout ?? 0) + (tiles?.archived ?? 0)} loading={loading} accent="text-ink-muted" />
    </div>
  );
}

function Tile({ label, value, loading, accent = 'text-ink-primary', icon }: {
  label: string; value: number | undefined; loading: boolean; accent?: string; icon?: React.ReactNode;
}) {
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between">
        <div className="eyebrow mb-0.5">{label}</div>
        {icon}
      </div>
      {loading
        ? <Sk className="mt-2 h-8 w-14" />
        : <div className={`text-2xl font-bold ${accent}`}>{value ?? 0}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Report window selector (AC6)
// ---------------------------------------------------------------------------

function ReportWindowSelector({ value, onChange, customFrom, setCustomFrom, customTo, setCustomTo }: {
  value: ReportWindowKey;
  onChange: (v: ReportWindowKey) => void;
  customFrom: string; setCustomFrom: (s: string) => void;
  customTo: string; setCustomTo: (s: string) => void;
}) {
  return (
    <div className="card flex flex-wrap items-center gap-3 p-3">
      <span className="text-xs uppercase tracking-wider text-ink-muted">Report window</span>
      <div className="flex flex-wrap gap-1">
        {WINDOW_OPTIONS.map((w) => (
          <button
            key={w.key}
            type="button"
            onClick={() => onChange(w.key)}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${value === w.key
              ? 'bg-accent-50 text-accent-800'
              : 'text-ink-secondary hover:bg-surface-muted'
              }`}
          >
            {w.label}
          </button>
        ))}
      </div>
      {value === 'custom' && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-ink-muted">From</label>
          <input type="date" className="input py-1 text-xs"
            value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
          <label className="text-xs text-ink-muted">To</label>
          <input type="date" className="input py-1 text-xs"
            value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Enrollment Funnel Widget (AC7) — site aggregate (per-study pivot deferred)
// ---------------------------------------------------------------------------

const FUNNEL_METRICS: { key: keyof NonNullable<Awaited<ReturnType<typeof studies.portfolioSummary>>>['funnel']; label: string }[] = [
  { key: 'total_screened', label: 'Total Screened' },
  { key: 'in_screening', label: 'In Screening' },
  { key: 'active', label: 'Active' },
  { key: 'screen_failed', label: 'Screen Failed' },
  { key: 'randomized', label: 'Randomized' },
  { key: 'dropped', label: 'Dropped' },
  { key: 'completed', label: 'Completed' },
];

function EnrollmentFunnelWidget({ loading, funnel }:
  { loading: boolean; funnel?: Record<string, number> }) {
  return (
    <div className="card p-4">
      <div className="mb-3 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-ink-muted" />
        <h2 className="text-sm font-semibold text-ink-primary">Aggregate enrollment funnel</h2>
        <span className="text-[10px] uppercase tracking-wider text-ink-muted ml-2">site roll-up</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
        {FUNNEL_METRICS.map((m) => (
          <div key={m.key} className="rounded-md border border-surface-border bg-surface-muted/40 p-2.5 text-center">
            <div className="text-[10px] uppercase tracking-wider text-ink-muted">{m.label}</div>
            {loading
              ? <Sk className="mx-auto mt-1.5 h-6 w-10" />
              : <div className="text-xl font-bold text-ink-primary">{funnel?.[m.key] ?? 0}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lifecycle multi-checkbox filter (Sprint planning: replace tabs with checkboxes)
// ---------------------------------------------------------------------------

function LifecycleCheckboxFilter({
  selected,
  onChange,
}: {
  selected: Set<string>;
  onChange: (v: Set<string>) => void;
}) {
  function toggle(key: string) {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  }

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-surface-border px-4 py-3">
      <span className="text-xs font-medium text-ink-muted whitespace-nowrap">Filter by status</span>
      {selected.size > 0 && (
        <button
          type="button"
          className="text-xs text-ink-muted hover:text-danger-700 underline underline-offset-2"
          onClick={() => onChange(new Set())}
        >
          Clear all
        </button>
      )}
      <div className="flex flex-wrap gap-2">
        {LIFECYCLE_OPTIONS.map((opt) => {
          const active = selected.has(opt.key);
          return (
            <label
              key={opt.key}
              className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors select-none
                ${active
                  ? 'border-accent-500 bg-accent-50 text-accent-700'
                  : 'border-surface-border bg-white text-ink-secondary hover:border-accent-300 hover:text-ink-primary'
                }`}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={active}
                onChange={() => toggle(opt.key)}
              />
              {opt.label}
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Study Table (AC2, AC3, AC4, AC5, AC10, AC9 search filter inline above)
// ---------------------------------------------------------------------------

/**
 * Fixed column widths for the Studies table (and its loading skeleton):
 * Protocol / Sponsor takes a fixed share and wraps long titles; PI gets enough
 * room for a full name; Action fits "Resume onboarding".
 */
function PortfolioColumns() {
  return (
    <colgroup>
      <col className="w-[40%]" />
      <col className="w-[8.5rem]" />
      <col className="w-[11rem]" />
      <col className="w-[7rem]" />
      <col className="w-[12rem]" />
      <col className="w-[10.5rem]" />
    </colgroup>
  );
}

function StudyTable({ loading, error, rows, total, page, pageSize, hasMore, onPageChange, onPageSizeChange }: {
  loading: boolean; error: unknown; rows: StudyRow[]; total: number | null; page: number; pageSize: number; hasMore: boolean;
  onPageChange: (n: number) => void; onPageSizeChange: (s: number) => void;
}) {
  const navigate = useNavigate();
  if (loading) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] table-fixed text-sm">
          <PortfolioColumns />
          <thead className="border-b border-surface-border bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
            <tr>
              <th className="px-3 py-2">Protocol / Sponsor</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Activation</th>
              <th className="px-3 py-2">Enrollment</th>
              <th className="px-3 py-2">PI</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }).map((_, i) => (
              <tr key={i} className="border-b border-surface-border last:border-0">
                <td className="px-3 py-3.5">
                  <Sk className="h-3.5 w-28" />
                  <Sk className="mt-1.5 h-3 w-44 opacity-60" />
                </td>
                <td className="px-3 py-3.5"><Sk className="h-5 w-16 rounded-full" /></td>
                <td className="px-3 py-3.5">
                  <div className="flex items-center gap-2">
                    <Sk className="h-1.5 w-24" />
                    <Sk className="h-3 w-6 opacity-60" />
                  </div>
                </td>
                <td className="px-3 py-3.5"><Sk className="h-3 w-12" /></td>
                <td className="px-3 py-3.5"><Sk className="h-3 w-16" /></td>
                <td className="px-3 py-3.5 text-right"><Sk className="ml-auto h-3 w-14" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (error) {
    return (
      <div className={`flex items-start gap-2 p-4 text-sm ${errorToneClass(error)}`}>
        {isPermissionError(error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
        <span>{errorText(error, 'Failed to load studies.')}</span>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="p-10 text-center text-sm text-ink-muted">
        <FolderKanban className="mx-auto mb-2 h-10 w-10 opacity-50" />
        No studies match.
      </div>
    );
  }
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] table-fixed text-sm">
          <PortfolioColumns />
          <thead className="border-b border-surface-border bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
            <tr>
              <th className="px-3 py-2">Protocol / Sponsor</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Activation</th>
              <th className="px-3 py-2">Enrollment</th>
              <th className="px-3 py-2">PI</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const preActive = r.status === 'draft' || r.status === 'initiation';
              const encId = encryptId(r.study_id);
              const target = preActive ? `/studies/${encId}/onboarding` : `/studies/${encId}`;
              return (
                <tr
                  key={r.study_id}
                  onClick={() => navigate(target)}
                  className="cursor-pointer border-b border-surface-border last:border-0 hover:bg-surface-muted/40"
                >
                  <td className="px-3 py-3 align-top">
                    <div className="break-words font-semibold text-ink-primary">{r.title}</div>
                    <div className="break-words font-mono text-xs text-ink-muted">{r.protocol_number} · {r.sponsor}</div>
                  </td>
                  <td className="px-3 py-3 text-xs">
                    <span className={studyStatusBadgeClass(r.status)}>{humanStatus(r.status)}</span>
                  </td>
                  <td className="px-3 py-3 text-xs">
                    {(() => {
                      const pct = r.status === 'active' ? 100 : Math.min(100, r.activation_progress_pct ?? 0);
                      const barColor = pct === 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-accent-500' : pct > 0 ? 'bg-amber-400' : 'bg-surface-muted';
                      return (
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-24 overflow-hidden rounded-full bg-surface-muted">
                            <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-ink-muted tabular-nums">{pct}%</span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-3 text-xs">
                    <span className="font-medium">{r.enrollment_actual}</span>
                    <span className="text-ink-muted"> / {r.enrollment_target}</span>
                  </td>
                  <td className="px-3 py-3 text-xs text-ink-secondary">
                    {r.pi_name
                      ? <span className="flex items-center gap-1 whitespace-nowrap" title={r.pi_name}><User className="h-3 w-3 shrink-0 text-ink-muted" /><span className="truncate">{r.pi_name}</span></span>
                      : '—'}
                  </td>
                  <td className="px-3 py-3 text-right text-xs">
                    <span className="inline-flex items-center gap-1 text-accent-700 hover:underline font-medium">
                      {preActive
                        ? <><BookOpen className="h-3.5 w-3.5" />Resume onboarding</>
                        : <><ArrowRight className="h-3.5 w-3.5" />Open</>}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-surface-border px-4 py-3 text-xs text-ink-muted">
        <div className="flex items-center gap-3">
          {(() => {
            const from = (page - 1) * pageSize + 1;
            const to = (page - 1) * pageSize + rows.length;
            return total !== null
              ? <span>Showing {from}–{to} of <strong className="text-ink-primary">{total}</strong> {total === 1 ? 'study' : 'studies'}</span>
              : <span>Page {page} · {rows.length} rows</span>;
          })()}
          <div className="flex items-center gap-1.5">
            <span>Rows per page:</span>
            <select
              className="input py-0.5 text-xs w-16"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            className="btn-secondary py-1 px-2 text-xs disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            <ChevronLeft className="h-3 w-3" /> Prev
          </button>
          <button
            type="button"
            className="btn-secondary py-1 px-2 text-xs disabled:opacity-40"
            disabled={!hasMore}
            onClick={() => onPageChange(page + 1)}
          >
            Next <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Study Links Panel (AC11, AC12)
// ---------------------------------------------------------------------------

const LINK_CATEGORIES: StudyLinkCategory[] = ['lms', 'randomization', 'regulatory', 'other'];

/**
 * `studyId` scopes the panel to one study (e.g. embedded inside the study
 * workspace hub — punch-list b16, links saved with a study_id had nowhere
 * to display "inside the study" before this). Omit it for the cross-study
 * Portfolio dashboard view, which intentionally shows every link.
 */
export function StudyLinksPanel({ studyId, readOnly = false }: { studyId?: string; readOnly?: boolean } = {}) {
  const qc = useQueryClient();
  // studies.links_write guards create + delete on the backend. The grid also
  // admits the platform admins CAN_MANAGE_STUDY_LINKS omitted, so this
  // closes a button that was hidden from roles the API already allowed.
  const linkPerms = useMyPermissions();
  const canManage =
    linkPerms.holdsOperation('studies.links_write', CAN_MANAGE_STUDY_LINKS) && !readOnly;
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<{ label: string; url: string; category: StudyLinkCategory; study_id: string }>(
    { label: '', url: '', category: 'lms', study_id: studyId ?? '' },
  );
  const [linkErrors, setLinkErrors] = useState<{ label?: string; url?: string }>({});

  function validateLink(): boolean {
    const errs: { label?: string; url?: string } = {};
    if (!draft.label.trim()) errs.label = 'Label is required.';
    else if (draft.label.trim().length < 2) errs.label = 'Label must be at least 2 characters.';
    if (!draft.url.trim()) {
      errs.url = 'URL is required.';
    } else {
      try {
        const u = new URL(draft.url.trim());
        if (!['http:', 'https:'].includes(u.protocol)) errs.url = 'URL must start with http:// or https://';
      } catch {
        errs.url = 'Enter a valid URL (e.g. https://example.com)';
      }
    }
    setLinkErrors(errs);
    return Object.keys(errs).length === 0;
  }

  const linksQ = useQuery({
    queryKey: ['study-links', studyId ?? 'all'],
    queryFn: () => studyLinks.list(studyId ? { study_id: studyId } : {}),
  });
  // For the per-study picker in the Add Link modal — only fetch when modal is open
  // and we're not already scoped to one study (studyId locks the scope instead).
  const studiesQ = useQuery({
    queryKey: ['studies'],
    queryFn: () => studies.list(),
    enabled: adding && !studyId,
  });

  const grouped = useMemo(() => {
    const out: Record<StudyLinkCategory, Array<{ link_id: string; label: string; url: string }>> = {
      lms: [], randomization: [], regulatory: [], other: [],
    };
    for (const link of linksQ.data?.links ?? []) {
      out[link.category].push({ link_id: link.link_id, label: link.label, url: link.url });
    }
    return out;
  }, [linksQ.data]);

  const createMut = useMutation({
    mutationFn: () => studyLinks.create({
      label: draft.label,
      url: draft.url,
      category: draft.category,
      study_id: draft.study_id || undefined, // empty -> site-wide link
    }),
    onSuccess: () => {
      toast.success(`Link "${draft.label}" added`);
      setAdding(false);
      setDraft({ label: '', url: '', category: 'lms', study_id: studyId ?? '' });
      setLinkErrors({});
      qc.invalidateQueries({ queryKey: ['study-links'] });
    },
    onError: (err) => toast.error(errorText(err, 'Failed to add link')),
  });

  const deleteMut = useMutation({
    mutationFn: (linkId: string) => studyLinks.delete(linkId),
    onSuccess: () => {
      toast.success('Link removed');
      qc.invalidateQueries({ queryKey: ['study-links'] });
    },
    onError: (err) => toast.error(errorText(err, 'Failed to remove link')),
  });

  return (
    <div className="card">
      <div className="flex items-center justify-between border-b border-surface-border bg-surface-muted/50 px-4 py-3">
        <div className="flex items-center gap-2">
          <ExternalLink className="h-4 w-4 text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink-primary">Study Links</h2>
          <span className="text-[10px] uppercase tracking-wider text-ink-muted">opens in new tab</span>
        </div>
        {canManage && (
          <button type="button" className="btn-secondary text-xs" onClick={() => setAdding(true)}>
            <Plus className="h-3.5 w-3.5" /> Add link
          </button>
        )}
      </div>
      {linksQ.isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <Sk className="h-3 w-20 opacity-70" />
              {Array.from({ length: 2 }).map((_, j) => (
                <Sk key={j} className="h-8 w-full rounded-md" />
              ))}
            </div>
          ))}
        </div>
      )}
      {!linksQ.isLoading && (linksQ.data?.links.length ?? 0) === 0 && (
        <div className="p-6 text-center text-sm text-ink-muted">
          No links yet. Add LMS, randomization, regulatory, or other resources here.
        </div>
      )}
      {(linksQ.data?.links.length ?? 0) > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
          {LINK_CATEGORIES.map((cat) => {
            const items = grouped[cat];
            if (items.length === 0) return null;
            return (
              <div key={cat}>
                <div className="eyebrow mb-1">{humanLinkCategory(cat)}</div>
                <ul className="space-y-1">
                  {items.map((l) => (
                    <li key={l.link_id} className="flex items-center justify-between gap-2 rounded border border-surface-border bg-white px-2.5 py-1.5">
                      <a
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 truncate text-sm text-accent-700 hover:underline"
                      >
                        {l.label}
                      </a>
                      {canManage && (
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Remove "${l.label}"?`)) deleteMut.mutate(l.link_id);
                          }}
                          className="text-ink-muted hover:text-danger-700"
                          aria-label="Remove link"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {adding && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
          <form
            onSubmit={(e) => { e.preventDefault(); if (validateLink()) createMut.mutate(); }}
            className="card w-full max-w-md space-y-4 p-6"
          >
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-semibold">Add Study Link</h3>
              <button type="button" onClick={() => { setAdding(false); setLinkErrors({}); }}><X className="h-4 w-4" /></button>
            </div>
            <div>
              <label className="label text-xs">Label *</label>
              <input
                className={`input ${linkErrors.label ? 'border-danger-500 focus:ring-danger-300' : ''}`}
                maxLength={120}
                value={draft.label}
                onChange={(e) => { setDraft({ ...draft, label: e.target.value }); setLinkErrors((p) => ({ ...p, label: undefined })); }}
                placeholder="External LMS training portal"
              />
              {linkErrors.label && <p className="mt-1 text-xs text-danger-500">{linkErrors.label}</p>}
            </div>
            <div>
              <label className="label text-xs">URL *</label>
              <input
                className={`input ${linkErrors.url ? 'border-danger-500 focus:ring-danger-300' : ''}`}
                value={draft.url}
                onChange={(e) => { setDraft({ ...draft, url: e.target.value }); setLinkErrors((p) => ({ ...p, url: undefined })); }}
                placeholder="https://lms.example.com/study"
              />
              {linkErrors.url && <p className="mt-1 text-xs text-danger-500">{linkErrors.url}</p>}
            </div>
            <div>
              <label className="label text-xs">Category</label>
              <select className="input"
                value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value as StudyLinkCategory })}>
                {LINK_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{humanLinkCategory(c)}</option>
                ))}
              </select>
            </div>
            {studyId ? (
              <p className="text-[10px] text-ink-muted">
                This link will be scoped to this study.
              </p>
            ) : (
              <div>
                <label className="label text-xs">Scope</label>
                <select className="input"
                  value={draft.study_id}
                  onChange={(e) => setDraft({ ...draft, study_id: e.target.value })}>
                  <option value="">Site-wide (all studies)</option>
                  {studiesQ.data?.rows.map((s) => (
                    <option key={s.study_id} value={s.study_id}>
                      {studyLabel(s)}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[10px] text-ink-muted">
                  Leave site-wide for resources that apply across all studies, or tie to a single study.
                </p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => { setAdding(false); setLinkErrors({}); }}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={createMut.isPending}>
                {createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <LinkIcon className="h-4 w-4" />}
                Add link
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
